import { useMemo } from "react";
import {
  Coordinate,
  PointPopup,
  RenderPointType,
  RenderRouteType,
  RoutePopup,
  RouteTransportTypes,
} from "@qubictron/maplib";
import { SeaTransportTypesModel } from "entities/shared/seaCard.types";
import { SearchReferenceModel } from "entities/shared/shipmentCard.types";
import { filteredCardsStore } from "features/Filter";
import { openedCardStore } from "features/OpenedCard";
import { useCardHandlers } from "features/Panel";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { tagsStore } from "features/Tags";
import { appStore } from "entities/App";
import { authStore } from "entities/Auth";
import { panelStore, PanelViewsList } from "entities/Panel";
import { searchFilterStore } from "entities/SearchFilter";
import { isSeaSavedShipmentModel, seaSavedCardStore } from "entities/SeaSavedCard";
import { seaSearchCardStore, useOpenedCardSearchQuery } from "entities/SeaSearchCard";
import { MapPoints } from "entities/shared/mapPointsModel";
import { RouteReturnType } from "entities/shared/mapRouteModel";
import { subscriptionStore } from "entities/Subscription";
import { useAppSelector } from "shared/lib/redux";
import { Nullable, ValueOf } from "shared/lib/typeCheck.lib";
import { CurrentPinIcon } from "shared/ui/Icons/Icons";
import styles from "../ui/Map.module.scss";

type UseSeaMapContentProps = {
  isMobile: boolean;
  showPanel: () => void;
  isMapLoaded: boolean;
  isDisabled: boolean;
};

export type RouteMarkerPoint = {
  route: Coordinate;
  transport: Nullable<ValueOf<typeof SeaTransportTypesModel>>;
};

type UseSeaMapContentReturnType = {
  isSeaRouteError: boolean;
  showSeaRoute: (reference: SearchReferenceModel) => void;
  points: RenderPointType[];
  renderRoute: RenderRouteType;
  renderRoutePoints: RenderPointType[];
  currentPin: Nullable<Coordinate>;
};

export const useSeaMapContent = ({
  isMobile,
  showPanel,
  isMapLoaded,
  isDisabled,
}: UseSeaMapContentProps): UseSeaMapContentReturnType => {
  const currentView = useAppSelector(panelStore.selectors.currentView);
  const shipment = useAppSelector(searchFilterStore.selectors.shipment);
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const openedCard = useAppSelector(openedCardStore.selectors.openedCard);
  const searchCards = useAppSelector(seaSearchCardStore.selectors.searchCards);
  const savedCards = useAppSelector(seaSavedCardStore.selectors.savedCards);
  const filteredCards = useAppSelector(filteredCardsStore.selectors.filteredCards);
  const cardsByTag = useAppSelector(tagsStore.selectors.cardsByTags);
  const { isCurrentViewMain, isCurrentViewOpenedCard, isCurrentViewFilteredCards, isCurrentViewCardsByTag } =
    usePanelViews();

  const { openCard } = useCardHandlers({
    openedCardAction: openedCardStore.actions.setOpenedCardAction,
  });

  const {
    searchResponse: cardData,
    isSearchSuccess,
    isSearchError,
  } = useOpenedCardSearchQuery({
    isAuthenticated,
    setSubscription: (subscription) => ({
      type: subscriptionStore.actions.setSubscription.type,
      payload: subscription,
    }),
    setActivePaymentModal: (activePaymentModal) => ({
      type: subscriptionStore.actions.setActivePaymentModal.type,
      payload: activePaymentModal,
    }),
    setAppIntegrationRequestLimitReached: (appIntegrationRequestLimitReached) => ({
      type: appStore.actions.setAppIntegrationRequestLimitReached.type,
      payload: appIntegrationRequestLimitReached,
    }),
    setWebAppRequestLimitReached: (webAppRequestLimitReached) => ({
      type: appStore.actions.setWebAppRequestLimitReached.type,
      payload: webAppRequestLimitReached,
    }),
  });

  const mapData = useMemo(() => {
    switch (currentView) {
      case PanelViewsList.main:
        return { searchCards, savedCards };
      case PanelViewsList.cardsByTag:
        return { savedCards: cardsByTag.filter((card) => isSeaSavedShipmentModel(card)) };
      case PanelViewsList.filteredCards:
        return { searchCards, savedCards: filteredCards.filter((card) => isSeaSavedShipmentModel(card)) };
      default:
        return { searchCards: [], savedCards: [] };
    }
  }, [currentView, searchCards.length, savedCards.length, filteredCards.length, cardsByTag.length]);

  const points = useMemo(() => {
    if (!isMapLoaded || isDisabled) return [];

    if (!isCurrentViewMain && !isCurrentViewFilteredCards && !isCurrentViewCardsByTag) {
      return [];
    }

    const points = new MapPoints({
      searchCards: mapData.searchCards ?? [],
      savedCards: mapData.savedCards ?? [],
      type: RouteTransportTypes.SEA,
    }).points;

    return points.map((point) => ({
      ...point,
      popup: (
        <RoutePopup
          from={point.fromDirection ?? undefined}
          to={point.toDirection ?? undefined}
          shipmentType={RouteTransportTypes.SEA}
          reference={point.reference ?? ""}
          onClick={() => showRoute(point.reference ?? "")}
        />
      ),
    }));
  }, [currentView, mapData?.searchCards?.length, mapData?.savedCards?.length, isMapLoaded, shipment]);

  const route = useMemo((): RouteReturnType => {
    const route = { points: [], route: { route: [] }, deflectionAngle: 0 };
    if (!isMapLoaded || isDisabled) return route;
    if (!isCurrentViewOpenedCard && !cardData) return route;
    return {
      ...(cardData?.route?.renderRoute ?? route),
      deflectionAngle: cardData?.route?.deflectionAngle ?? 0,
    };
  }, [currentView, cardData?.rawData, openedCard, isMapLoaded, points.length]);

  const showRoute = (reference: SearchReferenceModel) => {
    const targetCard =
      searchCards.find((card) => card.reference === reference) ??
      savedCards.find((card) => card.reference === reference);
    if (targetCard) {
      openCard(targetCard);
    }
    if (isMobile) {
      showPanel();
    }
  };

  const renderRoutePoints = useMemo(() => {
    return route.points.map((point) => ({
      ...point,
      popup: point.isCurrentPin ? (
        <RoutePopup
          from={cardData?.fromDirection ?? undefined}
          to={cardData?.toDirection ?? undefined}
          shipmentType={RouteTransportTypes.SEA}
          reference={cardData?.reference ?? ""}
        />
      ) : (
        <PointPopup label={point.label ?? ""} shipmentType={RouteTransportTypes.SEA} />
      ),
      marker: point.isCurrentPin ? (
        <CurrentPinIcon rotation={route.deflectionAngle} className={styles.currentPinIcon} />
      ) : undefined,
    }));
  }, [route.points, route.deflectionAngle, cardData?.fromDirection, cardData?.toDirection, cardData?.reference]);

  return {
    showSeaRoute: showRoute,
    points,
    renderRoute: route.route,
    renderRoutePoints,
    currentPin: cardData?.currentPin ?? null,
    isSeaRouteError:
      isCurrentViewOpenedCard &&
      (isSearchError || (isSearchSuccess && (!cardData?.isSuccess || !cardData?.currentPin || !cardData?.routeData))),
  };
};
